import {
  Box,
  IconButton,
  ListItem,
  ListItemButton,
  SxProps,
  Typography,
  useTheme,
} from '@mui/material';
import { FC } from 'react';
import { NotificationModel, NotificationTypes } from '@/logic/services/notifications';

import Icon24 from '@/components/shared/icon-24';
import { ImageBoxFuture } from '../../shared/image-box-future';
import TrashIcon from '/public/icons/notifications/trash-red-icon.svg';
import { pxToRem } from '@/theme/typography';

interface NotificationItemProps {
  notification: NotificationModel;
  handleNotificationClick: (notification: NotificationModel) => void;
  handleNotificationImage: (type: NotificationTypes) => string | File | FC;
  calculateNotificationTime: (date: string) => string;
  onDeleteNotification: (id: string) => void;
}
const NotificationItem: FC<NotificationItemProps> = ({
  notification,
  handleNotificationClick,
  handleNotificationImage,
  calculateNotificationTime,
  onDeleteNotification,
}) => {
  const theme = useTheme();

  const sx: SxProps = {
    fontWeight: 400,
    fontSize: {
      mobile: '14px',
      tablet: pxToRem(14),
    },
    lineHeight: '20px',
    inlineSize: {
      mobile: '60vw',
      tablet: pxToRem(316),
    },
    textAlign: 'start',
  };

  return (
    <>
      <ListItem
        disablePadding
        secondaryAction={
          <IconButton
            color='primary'
            component='button'
            sx={{
              width: '24px',
              height: '24px',
              padding: 2,
            }}
            onClick={() => onDeleteNotification(notification.id)}
          >
            <Icon24 icon={TrashIcon} title='global.delete_icon' />
          </IconButton>
        }
      >
        <ListItemButton
          onClick={() => handleNotificationClick(notification)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'start',
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                position: 'relative',
                marginBlock: pxToRem(12),
                width: '76px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              {!notification.isRead && (
                <Box
                  sx={{
                    position: 'absolute',
                    zIndex: 1,
                    padding: '2px',
                    backgroundColor: 'white',
                    borderRadius: '100%',
                    transform: `translate(${
                      notification.imageUrl ? '-220%' : '-120%'
                    },-120%)`,
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor: '#E0004D',
                      width: {
                        mobile: '12px',
                        tablet: pxToRem(12),
                      },
                      height: {
                        mobile: '12px',
                        tablet: pxToRem(12),
                      },
                      borderRadius: '100%',
                    }}
                  />
                </Box>
              )}
              {notification.imageUrl ? (
                <ImageBoxFuture
                  img={notification?.imageUrl}
                  title={notification?.title}
                  objectFit='contain'
                  sx={{
                    width: { mobile: '76px', tablet: pxToRem(76) },
                    height: { mobile: '38px', tablet: pxToRem(38) },
                  }}
                />
              ) : (
                <ImageBoxFuture
                  img={handleNotificationImage(notification.type as NotificationTypes)}
                  title={notification.title}
                  objectFit='contain'
                  sx={{
                    width: { mobile: '48px', tablet: pxToRem(48) },
                    height: { mobile: '48px', tablet: pxToRem(48) },
                  }}
                />
              )}
            </Box>
            <Box
              sx={{
                marginInlineStart: {
                  mobile: '16px',
                  tablet: pxToRem(16),
                },
              }}
            >
              <Typography
                sx={{
                  ...sx,
                  color: theme.palette.grey[90],
                  fontWeight: 700,
                }}
              >
                {notification.title}
              </Typography>
              <Typography
                sx={{
                  ...sx,
                  color: theme.palette.grey[60],
                  overflowWrap: 'anywhere',
                }}
              >
                {notification.body}
              </Typography>
              <Typography
                sx={{
                  ...sx,
                  color: theme.palette.grey[40],
                }}
              >
                {calculateNotificationTime(notification.createdAt)}
              </Typography>
            </Box>
          </Box>
        </ListItemButton>
      </ListItem>
    </>
  );
};

export default NotificationItem;
